import { Box, Button, SelectInput } from '@palmetto/palmetto-components';
import { useCallback, useMemo } from 'react';

import {
  ApprovedVendorAllocation,
  type ApprovedVendorSettingValue,
  DomesticContentMountingHardwareLabels,
  DomesticContentMountingHardwareType,
  VendorEquipmentTypes,
} from 'types';

export interface OrgHardwareEntrySelectorProps {
  avlEntries: Record<string, ApprovedVendorSettingValue[]>;
  selectedEntryIds: string[];
  onSelectionChange: (entryIds: string[]) => void;
  allowedAllocations?: ApprovedVendorAllocation[];
  disabled?: boolean;
}

interface FlattenedEntry {
  id: string;
  legacyIds?: string[];
  manufacturer: string;
  modelNumber: string;
  name: string;
  sku?: string;
  description?: string;
  category?: string;
  allocation: ApprovedVendorAllocation;
  type: VendorEquipmentTypes;
  groupLabel: string;
}

// A pinned entry id resolves against the item's canonical id or any of its legacy ids.
const isEntrySelected = (entry: FlattenedEntry, selectedEntryIds: string[]): boolean =>
  selectedEntryIds.includes(entry.id) ||
  (entry.legacyIds?.some((legacyId) => selectedEntryIds.includes(legacyId)) ?? false);

const typeGroupLabels: Record<string, string> = {
  [VendorEquipmentTypes.panel]: 'Solar Modules',
  [VendorEquipmentTypes.inverter]: 'Inverters',
  [VendorEquipmentTypes.storage]: 'Batteries',
};

const PLAIN_MOUNTING_GROUP = 'Mounting Hardware';

const allocationDisplayNames: Record<string, string> = {
  [ApprovedVendorAllocation.limited]: 'Limited',
  [ApprovedVendorAllocation.exception]: 'Exception',
  [ApprovedVendorAllocation.preApproval]: 'Pre-Approval',
  [ApprovedVendorAllocation.safeHarbor]: 'Safe Harbor',
  [ApprovedVendorAllocation.deprecated]: 'Deprecated',
};

const relevantTypes = [
  VendorEquipmentTypes.panel,
  VendorEquipmentTypes.inverter,
  VendorEquipmentTypes.storage,
  VendorEquipmentTypes.mounting,
];

// Stable display order for group headings: the existing types first, then the
// mounting categories, then the plain (empty-category) mounting bucket.
const groupOrder = [
  'Solar Modules',
  'Inverters',
  'Batteries',
  DomesticContentMountingHardwareLabels[DomesticContentMountingHardwareType.rail],
  DomesticContentMountingHardwareLabels[DomesticContentMountingHardwareType.fastenerClamp],
  DomesticContentMountingHardwareLabels[DomesticContentMountingHardwareType.fastenerSplice],
  DomesticContentMountingHardwareLabels[DomesticContentMountingHardwareType.fastenerMlpe],
  PLAIN_MOUNTING_GROUP,
].map((label) =>
  label === 'Solar Modules' || label === 'Inverters' || label === 'Batteries' || label === PLAIN_MOUNTING_GROUP
    ? label
    : `Mounting Hardware — ${label}`,
);

const getGroupLabel = (type: VendorEquipmentTypes, category?: string): string => {
  if (type !== VendorEquipmentTypes.mounting) {
    return typeGroupLabels[type] ?? PLAIN_MOUNTING_GROUP;
  }

  const categoryLabel = DomesticContentMountingHardwareLabels[category as DomesticContentMountingHardwareType];
  return categoryLabel ? `Mounting Hardware — ${categoryLabel}` : PLAIN_MOUNTING_GROUP;
};

// Canonical AVL mounting-hardware label (matches mapAllAvlManufacturerOptions),
// plus the selector's allocation suffix. Uses a hyphen, not the em-dash used by
// the other equipment types.
const buildMountingEntryLabel = (entry: FlattenedEntry): string => {
  const identifier = entry.sku || entry.modelNumber || entry.name;
  const allocationLabel = allocationDisplayNames[entry.allocation] ?? entry.allocation;
  const description = entry.description ? ` - ${entry.description}` : '';
  return `${entry.manufacturer} - ${identifier}${description} [${allocationLabel}]`;
};

const buildEntryLabel = (entry: FlattenedEntry): string =>
  entry.type === VendorEquipmentTypes.mounting
    ? buildMountingEntryLabel(entry)
    : `${entry.manufacturer} — ${entry.modelNumber} [${allocationDisplayNames[entry.allocation] ?? entry.allocation}]`;

const buildRemoveAriaLabel = (entry: FlattenedEntry): string =>
  entry.type === VendorEquipmentTypes.mounting
    ? `Remove ${buildMountingEntryLabel(entry)}`
    : `Remove ${entry.manufacturer} ${entry.modelNumber}`;

const orderGroups = (grouped: Record<string, FlattenedEntry[]>): [string, FlattenedEntry[]][] => {
  const ordered: [string, FlattenedEntry[]][] = [];
  groupOrder.forEach((label) => {
    if (grouped[label]?.length) {
      ordered.push([label, grouped[label]]);
    }
  });
  return ordered;
};

const isCoveredByAllocation = (entry: FlattenedEntry, allowedAllocations: ApprovedVendorAllocation[]) =>
  allowedAllocations.includes(entry.allocation) || entry.allocation === ApprovedVendorAllocation.full;

const EMPTY_ALLOWED_ALLOCATIONS_ARRAY: ApprovedVendorAllocation[] = [];

const OrgHardwareEntrySelector = ({
  avlEntries,
  selectedEntryIds,
  onSelectionChange,
  allowedAllocations = EMPTY_ALLOWED_ALLOCATIONS_ARRAY,
  disabled = false,
}: OrgHardwareEntrySelectorProps) => {
  const flattenedEntries = useMemo(() => {
    const entries: FlattenedEntry[] = [];

    relevantTypes.forEach((type) => {
      const typeEntries = avlEntries[type] ?? [];
      typeEntries.forEach((entry) => {
        entries.push({
          id: entry.id,
          legacyIds: entry.legacyIds,
          manufacturer: entry.manufacturer,
          modelNumber: entry.modelNumber,
          name: entry.name,
          sku: entry.sku,
          description: entry.description,
          category: entry.category,
          allocation: entry.allocation,
          type,
          groupLabel: getGroupLabel(type, entry.category),
        });
      });
    });

    return entries;
  }, [avlEntries]);

  const selectedEntries = useMemo(
    () =>
      flattenedEntries.filter(
        (entry) => isEntrySelected(entry, selectedEntryIds) && !isCoveredByAllocation(entry, allowedAllocations),
      ),
    [flattenedEntries, selectedEntryIds, allowedAllocations],
  );

  const availableEntries = useMemo(
    () =>
      flattenedEntries.filter(
        (entry) => !isEntrySelected(entry, selectedEntryIds) && !isCoveredByAllocation(entry, allowedAllocations),
      ),
    [flattenedEntries, selectedEntryIds, allowedAllocations],
  );

  const groupedAvailableEntries = useMemo(() => {
    const grouped: Record<string, FlattenedEntry[]> = {};

    availableEntries.forEach((entry) => {
      (grouped[entry.groupLabel] ??= []).push(entry);
    });

    return orderGroups(grouped);
  }, [availableEntries]);

  const handleAddEntry = useCallback(
    (entryId: string) => {
      if (entryId && !selectedEntryIds.includes(entryId)) {
        onSelectionChange([...selectedEntryIds, entryId]);
      }
    },
    [selectedEntryIds, onSelectionChange],
  );

  const handleRemoveEntry = useCallback(
    (entryId: string) => {
      onSelectionChange(selectedEntryIds.filter((id) => id !== entryId));
    },
    [selectedEntryIds, onSelectionChange],
  );

  const selectOptions = useMemo(
    () =>
      groupedAvailableEntries.map(([groupLabel, entries]) => ({
        label: groupLabel,
        options: entries.map((entry) => ({
          label: buildEntryLabel(entry),
          value: entry.id,
        })),
      })),
    [groupedAvailableEntries],
  );

  const handleSelectChange = useCallback(
    (event: any) => {
      if (event?.target?.value?.value) {
        handleAddEntry(event.target.value.value);
      }
    },
    [handleAddEntry],
  );

  const groupedSelectedEntries = useMemo(() => {
    const grouped: Record<string, FlattenedEntry[]> = {};

    selectedEntries.forEach((entry) => {
      (grouped[entry.groupLabel] ??= []).push(entry);
    });

    return orderGroups(grouped);
  }, [selectedEntries]);

  return (
    <Box gap="md">
      {!disabled && (
        <SelectInput
          id="hardware-entry-search"
          label="Add Specific Hardware Entries"
          options={selectOptions}
          placeholder="Search by manufacturer or model..."
          value={null}
          isClearable
          onChange={handleSelectChange}
          menuPortalTarget={document.body}
        />
      )}

      <Box gap="md">
        {selectedEntries.length === 0 ? (
          <Box color="body-secondary" fontSize="sm">
            No hardware entries selected.
          </Box>
        ) : (
          <>
            {groupedSelectedEntries.map(([groupLabel, entries]) => {
              if (entries.length === 0) return null;

              return (
                <Box key={groupLabel} gap="xs">
                  <Box fontSize="sm" fontWeight="medium">
                    {groupLabel}
                  </Box>
                  <Box gap="xs">
                    {entries.map((entry) => {
                      const label = buildEntryLabel(entry);
                      return (
                        <Box
                          key={entry.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          padding="xs sm"
                          background="secondary"
                          radius="sm"
                        >
                          <Box fontSize="sm">{label}</Box>
                          {!disabled && (
                            <Button
                              variant="tertiary"
                              tone="neutral"
                              size="xs"
                              iconPrefix="remove"
                              aria-label={buildRemoveAriaLabel(entry)}
                              // oxlint-disable-next-line react-perf/jsx-no-new-function-as-prop
                              onClick={() => handleRemoveEntry(entry.id)}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </Box>
  );
};

export default OrgHardwareEntrySelector;
